import { useState } from "react";
import { Button } from "@xpense/ui";
import { api } from "../api/client";

interface Props {
  transactionId: string;
  merchant: string | null;
  amount: number;
  onSubmitted: () => void;
  onCancel: () => void;
}

export default function ItemizationForm({ transactionId, merchant, amount, onSubmitted, onCancel }: Props) {
  const [mode, setMode] = useState<"text" | "percentage">("text");
  const [textData, setTextData] = useState("");
  const [percentages, setPercentages] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    setSubmitting(true);

    const data = mode === "text" ? textData.trim() : percentages.trim();
    if (!data) {
      setError("Please provide item details");
      setSubmitting(false);
      return;
    }

    try {
      await api.post("/api/v1/nutrition/itemize", {
        transaction_id: transactionId,
        itemization_type: mode === "text" ? "itemized_text" : "percentage_breakdown",
        data,
      });
      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="xpn-card" style={{ padding: 16, marginTop: 8 }}>
      <div className="xpn-text-body-md xpn-text" style={{ fontWeight: 650, marginBottom: 4 }}>
        Itemize {merchant || "this transaction"} — ₹{amount}
      </div>
      <div className="xpn-text-caption xpn-text-muted" style={{ marginBottom: 12 }}>
        Help us understand what you purchased so we can track your nutrition.
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          className={`xpn-btn xpn-btn--sm ${mode === "text" ? "xpn-btn--primary" : "xpn-btn--secondary"}`}
          onClick={() => setMode("text")}
        >
          Paste items
        </button>
        <button
          className={`xpn-btn xpn-btn--sm ${mode === "percentage" ? "xpn-btn--primary" : "xpn-btn--secondary"}`}
          onClick={() => setMode("percentage")}
        >
          Percentage split
        </button>
      </div>

      {mode === "text" ? (
        <div>
          <textarea
            className="xpn-input"
            placeholder="e.g., 1kg apples, 500g paneer, 2L milk, 1 packet biscuits"
            value={textData}
            onChange={(e) => setTextData(e.target.value)}
            rows={3}
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="xpn-text-caption xpn-text-muted" style={{ marginBottom: 8 }}>
            Or paste an image of your bill (coming soon)
          </div>
        </div>
      ) : (
        <div>
          <textarea
            className="xpn-input"
            placeholder="e.g., 30% vegetables, 20% dairy, 15% grains, 15% snacks, 10% fruits, 10% other"
            value={percentages}
            onChange={(e) => setPercentages(e.target.value)}
            rows={2}
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="xpn-text-caption xpn-text-muted" style={{ marginBottom: 8 }}>
            Rough estimates are fine — the AI will handle the rest.
          </div>
        </div>
      )}

      {error && <div className="xpn-text-caption" style={{ color: "var(--xpn-color-danger)", marginBottom: 8 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <Button size="sm" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting..." : "Submit"}
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Skip
        </Button>
      </div>
    </div>
  );
}
